import { AI_TOOLS, MANUAL_EDIT_TOOLS, toolToPinturaFeatureMapRedesigned } from '../PhotoStudioUtils'
import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { EditionAction, EditionType, getEditionTypeString, getUsedEditionActions, pinturaFeatureToEditionAction } from './photoStudioTypes'
import { Endpoints, JobPollErrorType, JobType, useAIEraserProcess, useAIVisualsResult, useGetAssignmentVisualByTypeName, useJobPoll } from 'dataQueries'
import { FileType } from 'constants/misc'
import { PhotoStudioHistorySource, usePhotoStudioHistory } from './hooks/usePhotoStudioHistory.hook'
import { PinturaImageState, Size } from '@pqina/pintura'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { UploadType, getSignedURLRetrieverSeq, removeLastFileExtension } from 'components/common/FileAPI/fileUtils'
import { dataURLToFile, imageToDataURL, isLocalUrl, revokeBlobUrl, revokeAllBlobUrls, signedUrlToBlobUrl } from 'utils/image'
import { useLocation, useParams } from 'react-router-dom'

import { AIProcessingStep } from '../../common/useAIProgressMessages'
import { AI_LABEL_OPACITY_DEFAULT, AI_LABEL_OPACITY_MAX, AI_LABEL_OPACITY_MIN, AiLabelColour, AiLabelPosition, AiLabelState, AiLabelType } from './AiLabel/aiLabel.constants'
import { applyAiLabelToImage, shouldApplyAiLabel } from './AiLabel/applyAiLabelToImage'
import { Endpoint } from 'constants/API'
import { ExpandedVisualType } from 'components/pages/Gallery/_main/ClientGallery/ClientGalleryPhoto'
import { FeatureFlag, useFeatureFlag } from 'utils/featureFlags'
import { GenerationType } from 'constants/visualStaging/visualStagingTypes'
import { JobStatus } from 'constants/API'
import { Path } from 'constants/router'
import { PhotoStudioFileType } from 'constants/visual/photoStudio'
import { PhotoStudioSave, PinturaSave } from './photoStudioTypes'
import { PinturaEditor } from '@pqina/react-pintura'
import { ProcessingMode } from 'constants/visualStaging/visualStagingTypes'
import { PropertyTab } from 'components/pages/Property/ClientProperty/_main/contexts'
import { RoomType } from 'constants/visualStaging/visualStagingTypes'
import { StyleType } from 'constants/visualStaging/visualStagingTypes'
import { VirtualStagingState } from 'models/visualStaging/virtualStagingTypes'
import { VisualFileType } from 'constants/visual'
import constate from 'constate'
import { getCommonAnalyticsData as getCommonAnalyticsDataUtil } from './utils/photoStudioAnalytics.utils'
import { getPropertyPathname } from 'utils/helpers/getPropertyPathname'
import { logger } from 'utils/logging'
import { useFileAPI } from 'components/common/FileAPI'
import { useImageUpload } from './hooks/useImageUpload.hook'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'
import { DEFAULT_LAB_ENHANCE_SETTINGS, DEFAULT_LAB_STAGE_SETTINGS, LAB_SDK_ENGINE, LabEnhanceSettings, LabProgress, LabScene, LabStageSettings } from './lab/labSdk.types'
import { ALL_FEATURES_STORAGE_KEYS, LabSectionsOpen, readStoredFlag, writeStoredFlag } from './lab/labAllFeatures'
import { resolveLabRunSources, resolveLabWorkingImage } from './lab/labRunSource'
import { toLabRecordedSettings } from './lab/labRecordedSettings'
import { useLabEngine } from './lab/useLabEngine.hook'
import { resolveLabObjectsDetection, useLabEraserObjects } from './lab/useLabEraserObjects.hook'
import { useWorkspacePlan } from 'utils/hooks'
import type { AdjustEditorHandle } from './adjust/AdjustEditor.context'
import { ADJUST_BLUR_TOOL } from './adjust/adjust.types'

interface ExtendedFile extends File {
  tag: string
}

/** Enum representing the navigation paths for the ClientPhotoStudioNavTabs component. */
export enum NavigationPaths {
  VISUAL_EDITING = 'VISUAL_EDITING',
  VIDEO_GENERATION = 'VIDEO_GENERATION',
}

export enum PhotoStudioSidebarOption {
  STAGING = 'staging',
  ENHANCE = 'enhance',
  ERASER = 'eraser',
  /** The in-house Adjust editor's Blur tool, on its own (FeatureFlag.PHOTO_STUDIO_PINTURA_REPLACEMENT) */
  BLUR = 'blur',
  ADJUST = 'adjust',
}

/** The tools that put the Adjust editor on the canvas: edits apply as they are made, saved when the editor is left */
export const isAdjustEditorOption = (option: PhotoStudioSidebarOption) =>
  option === PhotoStudioSidebarOption.ADJUST || option === PhotoStudioSidebarOption.BLUR

/** Enum representing features that are coming soon */
export enum ComingSoonFeature {
  ENHANCE = 'enhance',
}

// Re-export EditionType, EditionAction and helpers for backward compatibility
export { EditionType, EditionAction, getEditionTypeString, generationTypeToEditionAction, pinturaFeatureToEditionAction } from './photoStudioTypes'

/** The in-house Adjust editor's edition actions as the comma-joined `editionAction` Pintura's changes produced */
const joinAdjustEditionActions = (actions: EditionAction[], selectedPinturaFeature: string | undefined) =>
  actions.length > 0 ? actions.join(',') : pinturaFeatureToEditionAction(selectedPinturaFeature)

/**
 * Photo Studio context for individual visual selected from Gallery/Property gallery
 */
export const [ClientPhotoStudioContextProvider, useClientPhotoStudio] = constate(({
  file,
  imageUrl,
  assignmentId,
  isSelfEditedVisual,
}: {
  file: File
  imageUrl: string
  assignmentId: string
  isSelfEditedVisual: boolean
}) => {

  const editorRef = useRef<PinturaEditor | null>(null)
  const clearUndoRedoHistoryRef = useRef<(() => void) | null>(null)
  const hasMaskDrawnRef = useRef<(() => boolean) | null>(null)
  // Ref to track mask analytics data (brushSize + numberOfMasksApplied) from MaskDrawer context
  const maskAnalyticsRef = useRef<{ brushSize: number, numberOfMasksApplied: number }>({ brushSize: 0, numberOfMasksApplied: 0 })
  // Ignore Pintura editor undo stack for save modal right after a successful save-and-switch
  const ignoreEditorHistoryForModalRef = useRef<boolean>(false)
  // In-house Adjust editor (FeatureFlag.PHOTO_STUDIO_PINTURA_REPLACEMENT): registered by AdjustEditorProvider while
  // Adjust is on screen — the save and auto-save paths read it where Pintura's paths read `editorRef`
  const adjustEditorRef = useRef<AdjustEditorHandle | null>(null)
  const [adjustHasEdits, setAdjustHasEdits] = useState(false)

  const { t } = useTranslation(['photo_studio_editing', 'common'])
  const { id = '' } = useParams<{ id: string }>()
  const location = useLocation()
  const isPropertyPath = getPropertyPathname(location.pathname)

  const { spawnErrorToast } = useSnackbar()
  const assignmentVisualByName = useGetAssignmentVisualByTypeName()

  const { clientData, userPropertyWorkspaceId, currentUserWorkspace } = useUserData()

  const [isProcessingImage, setIsProcessingImage] = useState(false)
  const [aiProcessingStep, setAIProcessingStep] = useState<AIProcessingStep | undefined>(undefined)
  /** Live stage/percent/ETA of a running Lab SDK order (null when idle or on the ai-visuals engine) */
  const [labProgress, setLabProgress] = useState<LabProgress | null>(null)
  /** The latest step of the running Lab order to stage over the photo (null between runs) */
  const [labScene, setLabScene] = useState<LabScene | null>(null)
  /**
   * The step image the run's scenes last put on screen (LabRunScenes), which the result's reveal wipes from. Not the
   * last one that came in: the scenes play slower than they arrive, so that one may still be queued. No render needs it.
   */
  const labSceneImageRef = useRef<string | null>(null)
  const showLabSceneImage = useCallback((url: string) => { labSceneImageRef.current = url }, [])
  /** Expected stage sequence of the running Lab order (drives the progress bar under the overlay text) */
  const [labStagePlan, setLabStagePlan] = useState<string[]>([])
  /** A freshly arrived Lab result to reveal over the previous image (cleared by the preview when the wipe ends) */
  const [labReveal, setLabReveal] = useState<{ from: string, to: string } | null>(null)
  /**
   * Engine switch: the Lab engine runs the AI tools when the LaunchDarkly flag is ON. The platform runs the Lab for us
   * (`lab/useLabEngine`) and checks the same flag server-side — the browser holds no Lab key.
   */
  const isLabSdkEngine = useFeatureFlag(FeatureFlag.ALLOW_PHOTO_STUDIO_LAB_SDK)
  const labEngine = useLabEngine(assignmentId, isLabSdkEngine)
  /** Adjust runs on the in-house editor (ClientPhotoStudio/adjust) instead of Pintura */
  const isPinturaReplacement = useFeatureFlag(FeatureFlag.PHOTO_STUDIO_PINTURA_REPLACEMENT)
  const [isSaving, setIsSaving] = useState(false)
  const [activeNavTab, setActiveNavTab] = useState<NavigationPaths>(NavigationPaths.VISUAL_EDITING)
  const [selectedPinturaFeature, setSelectedPinturaFeature] = useState<string | undefined>(undefined)

  const [selectedAIFeature, setSelectedAIFeature] = useState<string | undefined>(AI_TOOLS.VIRTUAL_STAGING)

  const [changes, setChanges] = useState<Partial<PinturaImageState>>({})
  const prevState = useRef<PinturaImageState | null>(null)

  const [pinturaHistory, setPinturaHistory] = useState<PinturaImageState[] | undefined>([])
  const [previewImage, setPreviewImageRaw] = useState<string>(imageUrl)
  const previousPreviewImageRef = useRef<string>(imageUrl)
  const setPreviewRequestIdRef = useRef(0)

  // Wrapped setter that converts signed URLs to blob Object URLs
  // for Mixpanel session recording compatibility (same-origin, non-expiring, stable)
  // NOTE: We intentionally do NOT revoke the previous blob URL here because
  // history entries still reference it (undo/redo needs all blob URLs alive).
  // All blob URLs are cleaned up on unmount via revokeAllBlobUrls().
  const setPreviewImage = useCallback((url: string) => {
    const requestId = ++setPreviewRequestIdRef.current

    if (isLocalUrl(url)) {
      previousPreviewImageRef.current = url
      setPreviewImageRaw(url)
      return
    }

    signedUrlToBlobUrl(url, { component: 'ClientPhotoStudio', action: 'setPreviewImage' })
      .then(blobUrl => {
        if (requestId === setPreviewRequestIdRef.current) {
          previousPreviewImageRef.current = blobUrl
          setPreviewImageRaw(blobUrl)
        } else {
          logger.info('Stale preview request discarded', { requestId, currentId: setPreviewRequestIdRef.current }, { sentry: false, context: { component: 'ClientPhotoStudio', action: 'setPreviewImage' } })
          revokeBlobUrl(blobUrl)
        }
      })
  }, [])

  // Convert the original imageUrl to a blob URL for the "Original" visual type toggle
  const [convertedImageUrl, setConvertedImageUrl] = useState<string>(imageUrl)

  useEffect(() => {
    let cancelled = false
    signedUrlToBlobUrl(imageUrl, { component: 'ClientPhotoStudio', action: 'convertInitialImageUrl' })
      .then(blobUrl => {
        if (!cancelled) {
          setConvertedImageUrl(blobUrl)
          // Also update the initial previewImage if it hasn't been changed yet
          if (previousPreviewImageRef.current === imageUrl) {
            previousPreviewImageRef.current = blobUrl
            setPreviewImageRaw(blobUrl)
          }
        }
      })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Cleanup all blob URLs when Photo Studio unmounts
  useEffect(() => {
    return () => revokeAllBlobUrls()
  }, [])

  const [hasAIEraserCompleted, setHasAIEraserCompleted] = useState(false)
  const [hasVisualStagingCompleted, setHasVisualStagingCompleted] = useState(false)
  const [hasEnhanceCompleted, setHasEnhanceCompleted] = useState(false)

  // Photo Studio history for undo/redo
  const photoStudioHistory = usePhotoStudioHistory(imageUrl, setPreviewImage, convertedImageUrl)
  /**
   * Lab SDK: the After / Before / Original candidates of the shown image — what the Generate chevron offers and
   * what the Compare slider shows as Before. Derived from the history, so undo/redo move it along.
   */
  const labRunSources = useMemo(
    () => resolveLabRunSources(photoStudioHistory.historyState, previewImage, convertedImageUrl),
    [photoStudioHistory.historyState, previewImage, convertedImageUrl]
  )

  const [tag, setTag] = useState<PhotoStudioSave>(PhotoStudioSave.PHOTO_STUDIO_EDIT_VISUAL)
  const [isFileVersioned, setIsFileVersioned] = useState(isSelfEditedVisual)
  const [latestPreviewFilenameState, setLatestPreviewFilenameState] = useState<string | null>(null)
  const latestPreviewFilenameRef = useRef<string | null>(null)
  // Track generation sequence number for analytics
  const [imageGenerationSequenceNumber, setImageGenerationSequenceNumber] = useState(0)

  const setLatestPreviewFilename = useCallback((filename: string | null) => {
    latestPreviewFilenameRef.current = filename
    setLatestPreviewFilenameState(filename)
  }, [])

  const latestPreviewFilename = latestPreviewFilenameState
  const [isSaveAndRedirecting, setIsSaveAndRedirecting] = useState(false)
  const [isToolSwitching, setIsToolSwitching] = useState(false)
  const [toolSwitchStep, setToolSwitchStep] = useState<string | null>(null)
  const [toolSwitchProgress, setToolSwitchProgress] = useState(0)

  const [expandedVisualType, setExpandedVisualType] = useState<ExpandedVisualType>(ExpandedVisualType.EDITED)
  const isOriginalVisualType = useMemo(() => expandedVisualType === ExpandedVisualType.ORIGINAL, [expandedVisualType])

  // AI label state (EU AI Act Art. 50(4)) - optional in every Photo Studio tool.
  // Rendered live by AiLabelOverlay and burned in on save by applyAiLabelToImage.
  const [aiLabelState, setAiLabelState] = useState<AiLabelState>({
    showAiLabel: false,
    labelType: AiLabelType.AI,
    labelText: t('ai_label.text_placeholder'),
    colour: 'black',
    opacity: AI_LABEL_OPACITY_DEFAULT,
    position: null,
    sizeFraction: null,
  })

  // Virtual Staging state (disclaimer removed - now uses unified disclaimer)
  const [virtualStagingState, setVirtualStagingState] = useState<VirtualStagingState>({
    generationType: GenerationType.DECLUTTER_ROOM,
    wallColor: null,
    floorTemplate: null,
    processingMode: ProcessingMode.ENTIRE_IMAGE,
    roomType: null,
    selectedStyle: null,
    keepFloorUntouched: true,
    isGeneratingStaging: false,
    maskData: null,
  })

  const convertedPinturaTag = useCallback((tag: string) => tag === PhotoStudioSave.PHOTO_STUDIO_RE_EDIT_VISUAL
    ? PinturaSave.PINTURA_RE_EDIT_VISUAL
    : PinturaSave.PINTURA_EDIT_VISUAL, [])

  const sourceVisualFilename = file.name

  // Unique session ID to group all Photo Studio events from open to close in Mixpanel
  const [photoStudioTrackId] = useState(() => crypto.randomUUID())

  const realtyId = isPropertyPath ? id : undefined

  const logPhotoStudioEvent = useCallback((eventName: string, params?: {}) => {
    logAnalyticsEvent(eventName, {
      userEmail: clientData?.email,
      organizationId: clientData?.organizationId,
      planId: currentUserWorkspace?.id,
      assignmentId,
      sourceVisualFilename,
      photoStudioTrackId,
      ...(realtyId && { realtyId }),
      ...params
    })
  }, [clientData?.email, clientData?.organizationId, currentUserWorkspace?.id, assignmentId, sourceVisualFilename, photoStudioTrackId, realtyId])

  /**
   * Lab SDK settings live here rather than in the panels, so they survive tool switches and the "Display all features
   * at once" view, which shows the same options in one list.
   */
  const [labStageSettings, setLabStageSettings] = useState<LabStageSettings>(DEFAULT_LAB_STAGE_SETTINGS)
  const [labEnhanceSettings, setLabEnhanceSettings] = useState<LabEnhanceSettings>(DEFAULT_LAB_ENHANCE_SETTINGS)

  const updateLabStageSettings = useCallback((patch: Partial<LabStageSettings>) => {
    setLabStageSettings((previous) => ({ ...previous, ...patch }))
    // Typing a described style is not a selection — and its text is the user's own words, never sent (length only)
    if (Object.keys(patch).every((key) => key === 'styleClause')) return
    logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_EDITION_SELECTED, {
      editionType: EditionType.VIRTUAL_STAGING,
      engine: LAB_SDK_ENGINE,
      editionSettings: toLabRecordedSettings(patch),
    })
  }, [logPhotoStudioEvent])

  const updateLabEnhanceSettings = useCallback((patch: Partial<LabEnhanceSettings>) => {
    setLabEnhanceSettings((previous) => ({ ...previous, ...patch }))
    logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_EDITION_SELECTED, {
      editionType: EditionType.ENHANCE,
      engine: LAB_SDK_ENGINE,
      editionSettings: patch,
    })
  }, [logPhotoStudioEvent])

  /**
   * Wrapper function to get common analytics data for photo studio events.
   * Uses the utility function from utils/photoStudioAnalytics.utils
   */
  const getCommonAnalyticsData = useCallback(async (
    propertyType?: string,
    roomType?: string | null,
    imageGenerationSequenceNumber?: number
  ) => {
    return getCommonAnalyticsDataUtil(
      file,
      sourceVisualFilename,
      clientData?.email,
      clientData?.organizationId,
      assignmentId,
      propertyType,
      roomType,
      imageGenerationSequenceNumber,
      photoStudioTrackId

    )
  }, [file, sourceVisualFilename, clientData?.email, clientData?.organizationId, assignmentId, photoStudioTrackId])

  // Factory function to create resolver with isAIEdited parameter
  const createUploadUrlResolver = useCallback((isAIEdited: boolean) => {
    return getSignedURLRetrieverSeq((fileEntry, api) => {
      return api.post(
        Endpoint.SAVE_SELF_EDITED_VISUAL,
        {
          assignmentId: assignmentId!.toString(),
          workspaceId: userPropertyWorkspaceId
        },
        {
          sourceVisualFilename: fileEntry.fileObject.name,
          contentType: fileEntry.fileObject.type,
          type: VisualFileType.POST,
          replaceExisting: isSelfEditedVisual && (fileEntry.fileObject as ExtendedFile).tag === PinturaSave.PINTURA_RE_EDIT_VISUAL,
          uploadType: UploadType.STANDARD,
          isAIEdited,
        },
        true // keep response to make sure the file is uploaded - otherwise response after getting signedUrl has different structure
      )
    })
  }, [assignmentId, isSelfEditedVisual, userPropertyWorkspaceId])

  const uploadEditedVisualToGallery = useFileAPI('SELF_EDITED_VISUALS', {
    uploadUrlResolver: createUploadUrlResolver(false),
  })

  const temporaryVisualUpload = useFileAPI('PHOTO_STUDIO_TEMPORARY_VISUAL', {
    uploadUrlResolver: getSignedURLRetrieverSeq((fileEntry, api) => api.post(
      Endpoints.AI_VISUALS_SAVE_TEMPORARY_VISUAL,
      { assignmentId: assignmentId!.toString() },
      {
        uploadType: UploadType.STANDARD,
        contentType: PhotoStudioFileType.TEMPORARY_VISUAL,
      },
      true
    ))
  })

  // Photo Studio Eraser - Mask upload for AI processing
  const uploadAIMask = useFileAPI('PHOTO_STUDIO_ERASER', {
    uploadUrlResolver: getSignedURLRetrieverSeq((fileEntry, api) => api.post(
      Endpoints.AI_VISUALS_MASK_UPLOAD_URL,
      { assignmentId: assignmentId!.toString() },
      {
        filename: fileEntry.fileObject.name,
        contentType: PhotoStudioFileType.MASK,
      },
      true,
      {
        params: {
          uploadType: UploadType.STANDARD
        }
      }
    ))
  })

  const fetchImageUrl = useCallback(async (visualName: string): Promise<string> => {
    const maxRetries = 10
    const retryDelay = 1500 // 3 seconds
    let lastError: unknown = null

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await assignmentVisualByName.mutateAsync({
          assignmentId,
          workspaceId: userPropertyWorkspaceId,
          visualType: VisualFileType.POST_WEB,
          visualName,
        })

        if (response.data?.url) {
          return response.data.url
        }

        // If response succeeded but no URL, treat as retryable condition
        // (image POST_WEB might not be generated in bucket yet)
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, retryDelay))
          continue
        }

        // No URL after all retries - log and return empty string
        const noUrlError = new Error('Response successful but no URL in response.data')
        lastError = noUrlError
        logger.error('Loading POST_WEB image for Photo Studio editing ERROR', noUrlError, {
          context: {
            component: 'ClientPhotoStudio',
            action: 'loadPostWebImage',
            metadata: {
              visualName,
              assignmentId,
              visualType: VisualFileType.POST_WEB,
              retryAttempts: attempt + 1,
              reason: 'missing_url_in_response'
            }
          }
        })

        spawnErrorToast(t('visual_loading:general_error', { error: noUrlError }))
      } catch (error) {
        lastError = error

        // If this is not the last attempt, wait before retrying
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, retryDelay))
          continue
        }

        // Only log and show error after all retries have failed
        logger.error('Loading POST_WEB image for Photo Studio editing ERROR', error, {
          context: {
            component: 'ClientPhotoStudio',
            action: 'loadPostWebImage',
            metadata: {
              visualName,
              assignmentId,
              visualType: VisualFileType.POST_WEB,
              retryAttempts: attempt + 1,
              reason: 'request_failed'
            }
          }
        })
        spawnErrorToast(t('visual_loading:general_error', { error }))
      }
    }

    throw lastError || new Error('Failed to load image after all retries')
  }, [assignmentId, assignmentVisualByName, spawnErrorToast, t, userPropertyWorkspaceId])

  // AI Eraser processing mutations
  const aiEraserProcess = useAIEraserProcess()
  const aiEraserJobPoll = useJobPoll(JobType.AI_ERASER)
  const aiVisualsResult = useAIVisualsResult()

  // State for tracking previous job and mask
  const [previousJobId, setPreviousJobId] = useState<string | null>(null)
  const [currentMaskFilename, setCurrentMaskFilename] = useState<string | null>(null)
  const [virtualStagingJobId, setVirtualStagingJobId] = useState<string | null>(null)

  const onExitStudio = useCallback((isFromSave: boolean = false) => {
    logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_EXIT, {
      exitType: isFromSave ? 'saved' : 'cancelled',
    })
    // Set state to indicate we're in save-and-redirect flow to prohibit UI changes
    setIsSaveAndRedirecting(true)

    // This is to ensure the new image is loaded properly
    setTimeout(() => {
      window.location.reload()
      if (isPropertyPath) {
        window.location.href = Path.PROPERTY_TAB_ASSIGNMENT.replace(':id', id).replace(':tab', PropertyTab.GALLERY).replace(':assignmentId', assignmentId.toString())
      } else {
        window.location.href = Path.GALLERY.replace(':id', assignmentId)
      }
    }, 1000)
  }, [isPropertyPath, id, assignmentId, logPhotoStudioEvent])

  // Use the extracted upload hook
  const { uploadAIToolImage, uploadPinturaImage, uploadAdjustImage, uploadFromHistory } = useImageUpload({
    uploadEditedVisualToGallery,
    assignmentId,
    sourceVisualFilename,
    selectedAIFeature,
    selectedPinturaFeature,
    isFileVersioned,
    setIsFileVersioned,
    onExitStudio,
    logPhotoStudioEvent,
    currentJobId: previousJobId,
    virtualStagingJobId,
    createUploadUrlResolver,
    onAISaveComplete: (responseData) => {
      setLatestPreviewFilename(responseData.filename)
    },
    onManualSaveComplete: (responseData) => {
      setLatestPreviewFilename(responseData.filename)
    },
    imageGenerationSequenceNumber,
    virtualStagingState,
    propertyType: undefined, // Will be passed from controller if needed
    file,
    historyEntries: photoStudioHistory.historyState.entries,
  })

  // Utility function to convert image URL to data URL (defined before handleUploadImage to avoid dependency issues)
  const convertImageToDataURL = useCallback(async (imageUrl: string, format: PhotoStudioFileType): Promise<string> => {
    return imageToDataURL(imageUrl, {
      format,
      logContext: {
        component: 'ClientPhotoStudio',
        action: 'createFullMaskFile',
        metadata: { imageUrl }
      }
    })
  }, [])

  // Shared upload function for both Pintura and AI/VS tools
  // Uses history-based save for AI tools to ensure we save exactly what's visible in preview,
  // regardless of undo state or tool switches.
  const handleUploadImage = useCallback(async (
    tag: string,
    isToolsSwitch: boolean,
    isAITool: boolean = false,
    overridePreviewUrl?: string
  ) => {
    if (isAITool) {
      setIsSaving(true)
      try {
        // Get the current history entry - this is the source of truth for what's visible in preview
        const currentHistoryEntry = photoStudioHistory.historyState.current

        if (!currentHistoryEntry) {
          throw new Error('No version available to save')
        }

        // Label eligibility follows the saved entry's provenance: any AI-edited result
        // can carry the optional AI label (all Photo Studio tools - decision 4 Aug)
        const isAiLabelEligible = currentHistoryEntry.source !== PhotoStudioHistorySource.ORIGINAL
          && currentHistoryEntry.source !== PhotoStudioHistorySource.ADJUST

        // FAIL CLOSED on an unusable label: a Text label with no text would silently ship an
        // unlabelled image while the toggle reads ON - block the save and tell the user instead.
        if (isAiLabelEligible && aiLabelState.showAiLabel && aiLabelState.labelType === AiLabelType.TEXT && !aiLabelState.labelText.trim()) {
          spawnErrorToast(t('ai_label.text_required'))
          setIsSaving(false)
          return
        }

        const applyAiLabel = isAiLabelEligible && shouldApplyAiLabel(aiLabelState)
          ? (src: string) => applyAiLabelToImage(src, aiLabelState)
          : undefined
        const aiLabelSettings = applyAiLabel
          ? {
            labelType: aiLabelState.labelType,
            colour: aiLabelState.colour,
            opacity: aiLabelState.opacity,
            position: aiLabelState.position,
            sizeFraction: aiLabelState.sizeFraction,
          }
          : undefined

        // Use the global history-based save which saves exactly what's visible in preview
        await uploadFromHistory(tag, currentHistoryEntry, isToolsSwitch, applyAiLabel, aiLabelSettings)

        // Clear AI Magic Eraser unsaved-change state after successful save
        if (selectedAIFeature === AI_TOOLS.MAGIC_ERASER) {
          setHasAIEraserCompleted(false)
          setPreviousJobId(null)
          setCurrentMaskFilename(null)
          if (clearUndoRedoHistoryRef.current) {
            clearUndoRedoHistoryRef.current()
          }
        }
        // Clear Visual Staging unsaved-change state after successful save
        if (selectedAIFeature === AI_TOOLS.VIRTUAL_STAGING) {
          setHasVisualStagingCompleted(false)
          setVirtualStagingJobId(null)
        }
        // Also clear these states regardless of currently selected tool
        // since we may have switched tools before saving
        if (hasAIEraserCompleted) {
          setHasAIEraserCompleted(false)
        }
        if (hasVisualStagingCompleted) {
          setHasVisualStagingCompleted(false)
        }
        if (hasEnhanceCompleted) {
          setHasEnhanceCompleted(false)
        }
        setIsSaving(false)
      } catch (error) {
        setIsSaving(false)
        logger.error('History-based save failed', error, {
          context: {
            component: 'ClientPhotoStudio',
            action: 'handleUploadImage',
            metadata: { isAITool, isToolsSwitch }
          }
        })
        // Surface the failure (e.g. AI label burn-in aborting the save) instead of failing silently
        spawnErrorToast(t('general_error', { error }))
        logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_SAVE_IMAGE, {
          status: 'failed',
          aiTool: selectedAIFeature,
          result: error,
        })
      }
    } else {
      const pinturaTag = convertedPinturaTag(tag)

      setIsSaving(true)

      try {
        // In-house editor: its document describes the edit (read before the upload replaces the photo it edits)
        const adjustEditor = isPinturaReplacement ? adjustEditorRef.current : null
        const adjustEditionAction = adjustEditor ? joinAdjustEditionActions(adjustEditor.getEditionActions(), selectedPinturaFeature) : undefined
        const resultImageUrl = adjustEditor
          ? await uploadAdjustImage(pinturaTag, await adjustEditor.exportFile(), adjustEditionAction, adjustEditor.getSaveAnalyticsFlags(), fetchImageUrl, setPreviewImage)
          : await uploadPinturaImage(pinturaTag, isToolsSwitch, editorRef, changes, fetchImageUrl, setPreviewImage)

        // Add new version to history when Pintura processing succeeds
        if (resultImageUrl) {
          const pinturaUsedActions = getUsedEditionActions(changes)
          const pinturaEditionAction = adjustEditor
            ? adjustEditionAction
            : pinturaUsedActions.length > 0
              ? pinturaUsedActions.join(',')
              : pinturaFeatureToEditionAction(selectedPinturaFeature)
          await photoStudioHistory.addVersion(resultImageUrl, PhotoStudioHistorySource.ADJUST, imageGenerationSequenceNumber, {
            editionAction: pinturaEditionAction
          })
        }

        // Clear tracked Pintura changes after successful save
        setPinturaHistory([])
        ignoreEditorHistoryForModalRef.current = true
        setIsSaving(false)
      } catch (error) {
        // Revert to previous version on error
        photoStudioHistory.revertToPreviousVersion()
        setIsSaving(false)
        logger.error('Pintura visual editing ERROR', error, {
          context: {
            component: 'ClientPhotoStudio',
            action: 'pinturaVisualEditing',
            metadata: {
              sourceVisualFilename,
              assignmentId,
              originalImageUrl: imageUrl
            }
          }
        })
        spawnErrorToast(t('general_error', { error }))
        logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_SAVE_IMAGE, {
          status: 'failed',
          aiTool: selectedAIFeature,
          pinturaTool: selectedPinturaFeature,
          result: error,
        })
      }
    }
  }, [uploadFromHistory, uploadPinturaImage, uploadAdjustImage, isPinturaReplacement, selectedAIFeature, selectedPinturaFeature, setIsSaving, editorRef, changes, fetchImageUrl, setPreviewImage, sourceVisualFilename, assignmentId, imageUrl, spawnErrorToast, t, logPhotoStudioEvent, photoStudioHistory, imageGenerationSequenceNumber, convertedPinturaTag, setHasAIEraserCompleted, setPreviousJobId, setCurrentMaskFilename, clearUndoRedoHistoryRef, setHasVisualStagingCompleted, setVirtualStagingJobId, setPinturaHistory, ignoreEditorHistoryForModalRef, hasAIEraserCompleted, hasVisualStagingCompleted, hasEnhanceCompleted, setHasEnhanceCompleted, aiLabelState])


  /**
   * The shown image as a JPEG File (Pintura's live edit while adjusting). `sourceImageUrl` overrides it for a Lab
   * run that starts from the Before / Original image instead of the preview.
   */
  const getPreviewFileForAutoSave = useCallback(async (sourceImageUrl?: string): Promise<File> => {
    if (!sourceImageUrl && selectedPinturaFeature && isPinturaReplacement && adjustEditorRef.current) {
      const adjustedFile = await adjustEditorRef.current.exportFile()
      return new File([adjustedFile], latestPreviewFilename || adjustedFile.name, {
        type: adjustedFile.type,
        lastModified: adjustedFile.lastModified,
      })
    }

    if (!sourceImageUrl && selectedPinturaFeature && editorRef.current) {
      const imageWriterResult = await editorRef.current.editor.processImage()
      const originalFile = imageWriterResult.dest
      if (originalFile) {
        const filename = latestPreviewFilename || originalFile.name
        return new File([originalFile], filename, {
          type: originalFile.type,
          lastModified: originalFile.lastModified,
        })
      }
    }

    const dataUrl = await convertImageToDataURL(sourceImageUrl ?? previewImage, PhotoStudioFileType.TEMPORARY_VISUAL)
    if (!dataUrl) {
      throw new Error('auto_save_missing_preview')
    }
    const safeBase = removeLastFileExtension(latestPreviewFilename || sourceVisualFilename || 'photo-studio')
    const filename = `${safeBase}-autosave${FileType.JPEG}`
    return dataURLToFile(dataUrl, filename)
  }, [selectedPinturaFeature, isPinturaReplacement, editorRef, latestPreviewFilename, convertImageToDataURL, previewImage, sourceVisualFilename])

  const uploadTemporaryVisualFile = useCallback((file: File) => {
    return new Promise<string>((resolve, reject) => {
      temporaryVisualUpload.uploadFiles([file], {
        onSettled: (successIds, errorIds, cancelledIds) => {
          if (successIds.length > 0) {
            resolve(successIds[0])
            return
          }

          if (errorIds.length > 0) {
            reject(new Error('temporary_visual_save_failed'))
            return
          }

          if (cancelledIds.length > 0) {
            reject(new Error('temporary_visual_save_cancelled'))
            return
          }

          reject(new Error('temporary_visual_save_failed'))
        }
      })
    })
  }, [temporaryVisualUpload])

  /** Lab engine eraser: uploads the mask (white on transparent, `lab/labMask.ts`); resolves with the stored filename */
  const uploadLabMask = useCallback((file: File) => new Promise<string>((resolve, reject) => {
    uploadAIMask.uploadFiles([file], {
      onSettled: (successIds, errorIds, cancelledIds) => {
        if (successIds.length > 0) {
          resolve(successIds[0])
          return
        }
        reject(new Error(cancelledIds.length > 0 && errorIds.length === 0 ? 'lab_mask_upload_cancelled' : 'lab_mask_upload_failed'))
      },
    })
  }), [uploadAIMask])

  const hasPendingChanges = useCallback(() => {
    const hasPinturaChanges = pinturaHistory && pinturaHistory.length > 0
    const editorHasChanges = !ignoreEditorHistoryForModalRef.current &&
      editorRef.current?.editor &&
      editorRef.current.editor.history.undo.length > 0
    const adjustEditorHasChanges = isPinturaReplacement && Boolean(adjustEditorRef.current?.hasEdits())

    return Boolean(hasPinturaChanges || editorHasChanges || adjustEditorHasChanges || hasAIEraserCompleted || hasVisualStagingCompleted || hasEnhanceCompleted)
  }, [pinturaHistory, isPinturaReplacement, hasAIEraserCompleted, hasVisualStagingCompleted, hasEnhanceCompleted])

  const autoSaveCurrentState = useCallback(async (isSwitchingFromAdjust: boolean = false) => {
    // Only show tool switching state when switching FROM Adjust (Pintura) to another tool
    // This is when we need to process the image, which takes time
    if (isSwitchingFromAdjust) {
      setIsToolSwitching(true)
      setToolSwitchStep('preparing')
    }

    try {
      // Keep preview consistent across tools without remote save
      // IMPORTANT: Don't clear change states here - preserve them so hasPendingChanges works across feature switches
      if (selectedPinturaFeature) {
        // Step 1: Preparing (already set above, but ensure it's visible)
        // Delay to ensure React renders the step and user can see it
        await new Promise(resolve => setTimeout(resolve, 500))

        // Step 2: Processing edits (this is the main step that takes time)
        setToolSwitchStep('processing')
        setToolSwitchProgress(0)

        // Calculate number of changes to estimate processing time more accurately
        const numberOfChanges = Object.keys(changes).length
        const undoHistoryLength = editorRef.current?.editor.history.undo.length || 0
        const totalEdits = numberOfChanges + undoHistoryLength
        // In-house editor: one full-size render, seconds rather than Pintura's tens of seconds
        const adjustEditor = isPinturaReplacement ? adjustEditorRef.current : null

        // Base duration: 20 seconds for minimal edits
        // Add 2 seconds per edit type, and 1 second per undo history item
        // Cap at 90 seconds maximum
        const baseDuration = 20000
        const durationPerChange = 2000
        const durationPerHistoryItem = 1000
        const estimatedDuration = adjustEditor
          ? Math.min(30000, 3000 + adjustEditor.getEditCount() * 500)
          : Math.min(
            90000, // Max 90 seconds
            baseDuration + (numberOfChanges * durationPerChange) + (undoHistoryLength * durationPerHistoryItem)
          )

        // Start fake progress for better UX during processing
        const startTime = Date.now()
        let lastProgress = 0

        const progressInterval = setInterval(() => {
          const elapsed = Date.now() - startTime
          const rawProgress = (elapsed / estimatedDuration) * 100
          const progress = Math.min(95, Math.max(0, rawProgress)) // Cap at 95% until done

          // Only update if progress increased (avoid going backwards)
          if (progress > lastProgress) {
            setToolSwitchProgress(progress)
            lastProgress = progress
          }
        }, 200) // Update every 200ms for smooth progress

        // Process image (this is async and may take time)
        let file: File
        try {
          file = await getPreviewFileForAutoSave()

          // Clear progress interval and set to 100%
          clearInterval(progressInterval)
          setToolSwitchProgress(100)
        } catch (error) {
          // Clear progress interval on error
          clearInterval(progressInterval)
          setToolSwitchProgress(0)
          throw error
        }

        // Step 3: Finalizing
        setToolSwitchStep('finalizing')
        // Small delay to show finalizing step
        await new Promise(resolve => setTimeout(resolve, 100))

        const localUrl = URL.createObjectURL(file)

        // Derive editionAction from Pintura changes (or the in-house editor's document) BEFORE adding to history
        const usedActions = getUsedEditionActions(changes)
        const editionAction = adjustEditor
          ? joinAdjustEditionActions(adjustEditor.getEditionActions(), selectedPinturaFeature)
          : usedActions.length > 0
            ? usedActions.join(',')
            : pinturaFeatureToEditionAction(selectedPinturaFeature)

        // Use startTransition to mark state updates as non-urgent
        // This prevents blocking the UI during tool switching
        React.startTransition(() => {
          setPreviewImage(localUrl)
          ignoreEditorHistoryForModalRef.current = true

          // Add new version to history when leaving Pintura for another tool
          photoStudioHistory.addVersion(localUrl, PhotoStudioHistorySource.ADJUST, undefined, { editionAction })
        })

        // Track ALL Pintura edits during tool switch — derive all used features from changes
        getCommonAnalyticsData().then(commonData => {
          logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_VISUAL_GENERATED, {
            editionType: EditionType.ADJUST,
            editionAction,
            ...commonData,
          })
        })
      }

      return latestPreviewFilenameRef.current || ''
    } catch (error) {
      setToolSwitchStep(null)
      setToolSwitchProgress(0)

      logger.error('Photo Studio auto save ERROR', error, {
        context: {
          component: 'ClientPhotoStudio',
          action: 'autoSaveCurrentState',
          metadata: {
            assignmentId,
            sourceVisualFilename,
            selectedAIFeature,
            selectedPinturaFeature
          }
        }
      })
      spawnErrorToast(t('general_error', { error }))
      throw error
    } finally {
      // Only clear tool switching state if we set it
      if (isSwitchingFromAdjust) {
        // Use startTransition to mark the state update as non-urgent
        // This allows the UI to remain responsive
        React.startTransition(() => {
          setIsToolSwitching(false)
          setToolSwitchStep(null)
          setToolSwitchProgress(0)
        })
      }
    }
  }, [getPreviewFileForAutoSave, selectedAIFeature, selectedPinturaFeature, isPinturaReplacement, setPreviewImage, assignmentId, sourceVisualFilename, spawnErrorToast, t, photoStudioHistory, changes, editorRef, getCommonAnalyticsData, logPhotoStudioEvent])

  const activeFeature = useMemo(() => {
    if (selectedAIFeature === AI_TOOLS.VIRTUAL_STAGING) {
      return PhotoStudioSidebarOption.STAGING
    }
    if (selectedAIFeature === AI_TOOLS.AUTO_ENHANCEMENT) {
      return PhotoStudioSidebarOption.ENHANCE
    }
    if (selectedAIFeature === AI_TOOLS.MAGIC_ERASER) {
      return PhotoStudioSidebarOption.ERASER
    }
    if (isPinturaReplacement && selectedPinturaFeature === ADJUST_BLUR_TOOL) {
      return PhotoStudioSidebarOption.BLUR
    }

    return PhotoStudioSidebarOption.ADJUST
  }, [selectedAIFeature, isPinturaReplacement, selectedPinturaFeature])

  const switchEditingMode = useCallback(async (target: PhotoStudioSidebarOption) => {
    if (isToolSwitching || target === activeFeature) return

    // Check if we're switching FROM Adjust (Pintura/manual tools) to another tool
    // This is when we need to process the image, which takes time
    const isSwitchingFromAdjust = isAdjustEditorOption(activeFeature) && !isAdjustEditorOption(target)
    // Adjust ⇄ Blur: the editor stays on the canvas with its document, so nothing is saved yet
    const isWithinAdjustEditor = isAdjustEditorOption(activeFeature) && isAdjustEditorOption(target)

    if (!isWithinAdjustEditor && hasPendingChanges()) {
      await autoSaveCurrentState(isSwitchingFromAdjust)
    }

    let editionType: EditionType | undefined
    let pinturaFeature: string | undefined
    switch (target) {
      case PhotoStudioSidebarOption.STAGING:
        setSelectedPinturaFeature(undefined)
        setSelectedAIFeature(AI_TOOLS.VIRTUAL_STAGING)
        editionType = EditionType.VIRTUAL_STAGING
        // Track edition selected for Virtual Staging with current generation type
        getCommonAnalyticsData(undefined, virtualStagingState.roomType || undefined).then(commonData => {
          logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_EDITION_SELECTED, {
            editionType: getEditionTypeString(EditionType.VIRTUAL_STAGING),
            editionSettings: {
              generationType: virtualStagingState.generationType,
            },
            ...commonData,
          })
        })
        break
      case PhotoStudioSidebarOption.ENHANCE:
        setSelectedPinturaFeature(undefined)
        setSelectedAIFeature(AI_TOOLS.AUTO_ENHANCEMENT)
        editionType = EditionType.ENHANCE
        break
      case PhotoStudioSidebarOption.ERASER:
        setSelectedPinturaFeature(undefined)
        setSelectedAIFeature(AI_TOOLS.MAGIC_ERASER)
        editionType = EditionType.MAGIC_ERASER
        // Track edition selected for Magic Eraser
        getCommonAnalyticsData().then(commonData => {
          logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_EDITION_SELECTED, {
            editionType: getEditionTypeString(EditionType.MAGIC_ERASER),
            ...commonData,
          })
        })
        break
      case PhotoStudioSidebarOption.BLUR:
        setSelectedAIFeature(undefined)
        setSelectedPinturaFeature(ADJUST_BLUR_TOOL)
        editionType = EditionType.ADJUST
        pinturaFeature = ADJUST_BLUR_TOOL
        getCommonAnalyticsData().then(commonData => {
          logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_EDITION_SELECTED, {
            editionType: getEditionTypeString(EditionType.ADJUST, ADJUST_BLUR_TOOL),
            editionSettings: { pinturaFeature: ADJUST_BLUR_TOOL },
            ...commonData,
          })
        })
        break
      case PhotoStudioSidebarOption.ADJUST:
      default:
        setSelectedAIFeature(undefined)
        const defaultPinturaFeature = toolToPinturaFeatureMapRedesigned[MANUAL_EDIT_TOOLS.FINE_TUNE]
        setSelectedPinturaFeature(defaultPinturaFeature)
        editionType = EditionType.ADJUST
        pinturaFeature = defaultPinturaFeature
        // Track edition selected for Adjust
        getCommonAnalyticsData().then(commonData => {
          logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_EDITION_SELECTED, {
            editionType: getEditionTypeString(EditionType.ADJUST, pinturaFeature),
            editionSettings: {
              pinturaFeature,
            },
            ...commonData,
          })
        })

        break
    }

  }, [isToolSwitching, activeFeature, hasPendingChanges, autoSaveCurrentState, logPhotoStudioEvent, getCommonAnalyticsData, virtualStagingState.generationType, virtualStagingState.roomType])

  const handleProcessMagicEraser = useCallback(async (
    maskedImageFile: File,
    onComplete?: (resultUrl: string) => Promise<string>
  ) => {
    // Run the AI generation process
    setIsProcessingImage(true)
    setHasAIEraserCompleted(false)
    setAIProcessingStep(AIProcessingStep.UPLOADING_MASK)

    try {
      // Step 1: Upload mask file using useFileAPI
      uploadAIMask.uploadFiles([maskedImageFile], {
        onSettled: async (successIds, errorIds, cancelledIds) => {
          if (successIds.length > 0) {
            const maskFilename = successIds[0]
            setAIProcessingStep(AIProcessingStep.PROCESSING)
            try {
              // Step 2: Call AI_ERASER endpoint to start processing
              // Use the latest saved preview filename if available (from VS/AI saves),
              // otherwise fall back to the original source visual filename
              const inputVisualFilename = latestPreviewFilenameRef.current || sourceVisualFilename

              const aiEraserResponse = await aiEraserProcess.mutateAsync({
                assignmentId,
                visualFilename: inputVisualFilename,
                maskFilename,
                ...(previousJobId && { reExecuteFromPreviousJob: previousJobId }),
                useTempBucket: true, // USE the temp bucket for the AI Eraser!!!
              })

              // Store the current job ID for potential re-execution
              setPreviousJobId(aiEraserResponse.id)
              setCurrentMaskFilename(maskFilename)

              // Step 3: Poll job until completion

              const jobResult = await aiEraserJobPoll.mutateAsync({
                jobId: aiEraserResponse.id
              })

              if (jobResult.status === JobStatus.FINISHED) {
                setAIProcessingStep(AIProcessingStep.FETCHING_RESULT)

                // Step 4: Get the Propershot result from AI_VISUALS_RESULT endpoint
                const result = await aiVisualsResult.refetchWithParams(
                  assignmentId,
                  aiEraserResponse.id
                )

                // Apply disclaimer if callback is provided (similar to Visual Staging)
                let finalResultUrl = result.signedURL
                if (onComplete) {
                  finalResultUrl = await onComplete(result.signedURL)
                }

                // Update preview with the processed image (preload to prevent delay)
                const img = new Image()
                img.onload = () => {
                  setPreviewImage(finalResultUrl)
                }
                img.src = finalResultUrl

                // Clear the mask and disable generate until new mask is used
                setCurrentMaskFilename(null)

                // Mark AI eraser as completed for Save button
                setHasAIEraserCompleted(true)

                // Track visual generated
                const newSequenceNumber = imageGenerationSequenceNumber + 1
                setImageGenerationSequenceNumber(newSequenceNumber)
                // Get common analytics data with sequence number
                getCommonAnalyticsData(undefined, undefined, newSequenceNumber).then(commonData => {
                  logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_VISUAL_GENERATED, {
                    editionType: EditionType.MAGIC_ERASER,
                    editionAction: EditionAction.ERASE,
                    ...commonData,
                  })
                })

                // Add new version to history with the result image
                await photoStudioHistory.addVersion(finalResultUrl, PhotoStudioHistorySource.MAGIC_ERASER, newSequenceNumber, {
                  editionAction: EditionAction.ERASE
                })

                // Mark processing as complete
                setIsProcessingImage(false)
                setAIProcessingStep(undefined)

              } else {
                const errorMessage = t('magic_eraser_error')
                logger.error('Magic Eraser processing failed', new Error(errorMessage), {
                  context: {
                    component: 'ClientPhotoStudio',
                    action: 'magicEraserProcessing',
                    metadata: {
                      assignmentId,
                      sourceVisualFilename,
                      jobId: aiEraserResponse.id
                    }
                  }
                })
                throw new Error(errorMessage)
              }
            } catch (aiError: unknown) {
              // Revert to previous version on error
              photoStudioHistory.revertToPreviousVersion()
              // Check if this is the validation error for missing visual filename or job ID
              const errorMessage = (aiError as Error)?.message
              if (errorMessage === 'photo_studio_editing.error_missing_visual_or_job_id') {
                const toastMessage = t('photo_studio_editing:error_missing_visual_or_job_id_toast')
                spawnErrorToast(toastMessage)
                setIsProcessingImage(false)
                setAIProcessingStep(undefined)
                return
              }

              // Extract error information from the API response
              const errorData = (aiError as { response: { data: { message: string, errorType: string, backendError: string } } })?.response?.data
              const apiError = errorData?.message
              const errorType = errorData?.errorType
              const backendError = errorData?.backendError

              let displayErrorMessage: string = t('magic_eraser_error')

              if (!!backendError) {
                displayErrorMessage = `${backendError} ${t('please_try_later')}`
              }
              else if (errorType === JobPollErrorType.POLLING_RETRY_LIMIT || errorType === JobPollErrorType.JOB_FAILED) {
                displayErrorMessage = t(`common:${apiError}`)
              }

              spawnErrorToast(displayErrorMessage)
              setAIProcessingStep(undefined)
            }
          } else if (errorIds.length > 0) {
            const errorMessage = `Failed to upload mask file: ${errorIds[0]}`
            logger.error('Mask file upload failed', new Error(errorMessage), {
              context: {
                component: 'ClientPhotoStudio',
                action: 'maskFileUpload',
                metadata: {
                  assignmentId,
                  sourceVisualFilename,
                  errorId: errorIds[0]
                }
              }
            })
            throw new Error(errorMessage)
          } else if (cancelledIds.length > 0) {
            const errorMessage = 'Mask upload was cancelled'
            logger.error('Mask upload was cancelled', new Error(errorMessage), {
              context: {
                component: 'ClientPhotoStudio',
                action: 'maskUploadCancelled',
                metadata: {
                  assignmentId,
                  sourceVisualFilename,
                  cancelledId: cancelledIds[0]
                }
              }
            })
            throw new Error(errorMessage)
          }

          setIsProcessingImage(false)
          setAIProcessingStep(undefined)
        }
      })
    } catch (error) {
      // Error during magic eraser processing

      logger.error('Photo Studio Magic Eraser ERROR', error, {
        context: {
          component: 'ClientPhotoStudio',
          action: 'magicEraserError',
          metadata: {
            sourceVisualFilename,
            assignmentId,
            originalImageUrl: imageUrl
          }
        }
      })

      // Revert to previous version on error
      photoStudioHistory.revertToPreviousVersion()

      spawnErrorToast(t('magic_eraser_error'))

      setIsProcessingImage(false)
      setAIProcessingStep(undefined)
    }
    // t, spawnErrorToast are not dependencies of the hook
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewImage, hasAIEraserCompleted, uploadAIMask, aiEraserProcess, assignmentId, sourceVisualFilename, previousJobId, aiEraserJobPoll, aiVisualsResult, imageUrl, setPreviewImage, setHasAIEraserCompleted, setIsProcessingImage, convertImageToDataURL, latestPreviewFilename])

  const handleUpdate = (imageState: PinturaImageState) => {
    if (!!selectedPinturaFeature && prevState.current) {
      // Any new manual edit should re-enable considering editor history for modal
      ignoreEditorHistoryForModalRef.current = false
      const updatedChanges: Partial<Record<keyof PinturaImageState, PinturaImageState[keyof PinturaImageState]>> = {}

      Object.keys(imageState).forEach((key) => {
        const typedKey = key as keyof PinturaImageState
        const newValue = imageState[typedKey]
        const oldValue = prevState.current ? prevState.current[typedKey] : undefined

        // Skip empty arrays, zero values, default backgroundColor [0, 0, 0, 0], and cropMaxSize/cropMinSize
        if (
          oldValue !== newValue &&
          !(Array.isArray(newValue) && newValue.length === 0) &&
          !(typeof newValue === 'number' && newValue === 0) &&
          !(typedKey === 'backgroundColor' && Array.isArray(newValue) && newValue.every((v) => v === 0)) &&
          typedKey !== 'cropMaxSize' && typedKey !== 'cropMinSize'
        ) {
          // Special case for crop comparison with editor image size
          if (typedKey === 'crop' && editorRef.current) {
            const imageSize = editorRef.current.editor.imageSize

            if (imageSize &&
              (newValue as Size)?.width === imageSize.width &&
              (newValue as Size)?.height === imageSize.height
            ) {
              return // Skip storing crop if it matches image size
            }
          }
          updatedChanges[typedKey] = newValue
        }
      })

      if (Object.keys(updatedChanges).length > 0) {
        setChanges((prev) => ({ ...prev, ...updatedChanges } as Partial<PinturaImageState>))
      }
    }
    prevState.current = imageState
  }

  // Function to reset AI completion state when generate starts
  const onGenerateStarted = useCallback(() => {
    setHasAIEraserCompleted(false)
  }, [])

  // Visual Staging state management functions
  const updateVirtualStagingState = useCallback((updates: Partial<VirtualStagingState>) => {
    setVirtualStagingState(prev => ({ ...prev, ...updates }))
  }, [])

  const setGenerationType = useCallback((generationType: GenerationType) => {
    updateVirtualStagingState({ generationType })
    logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_VISUAL_STAGING_GENERATION_TYPE_CHANGED, {
      generationType,
      assignmentId,
      sourceVisualFilename,
    })
  }, [updateVirtualStagingState, logPhotoStudioEvent, assignmentId, sourceVisualFilename])

  const setProcessingMode = useCallback((processingMode: ProcessingMode) => {
    updateVirtualStagingState({ processingMode })
    // Clear mask data if switching to entire image mode
    if (processingMode === ProcessingMode.ENTIRE_IMAGE) {
      updateVirtualStagingState({ maskData: null })
    }
    logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_VISUAL_STAGING_PROCESSING_MODE_CHANGED, {
      processingMode,
      assignmentId,
      sourceVisualFilename,
    })
    // Track edition selected
    getCommonAnalyticsData(undefined, virtualStagingState.roomType || undefined).then(commonData => {
      logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_EDITION_SELECTED, {
        editionType: EditionType.VIRTUAL_STAGING,
        editionSettings: {
          processingMode,
        },
        ...commonData,
      })
    })
  }, [updateVirtualStagingState, logPhotoStudioEvent, assignmentId, sourceVisualFilename, getCommonAnalyticsData, virtualStagingState.roomType])

  const setRoomType = useCallback((roomType: RoomType) => {
    updateVirtualStagingState({ roomType })
    // Reset selected style when room type changes as styles are room-specific
    updateVirtualStagingState({ selectedStyle: null })
    logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_VISUAL_STAGING_ROOM_TYPE_CHANGED, {
      roomType,
      assignmentId,
      sourceVisualFilename,
    })
    // Track edition selected
    getCommonAnalyticsData(undefined, roomType).then(commonData => {
      logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_EDITION_SELECTED, {
        editionType: EditionType.VIRTUAL_STAGING,
        editionSettings: {
          roomType,
        },
        ...commonData,
      })
    })
  }, [updateVirtualStagingState, logPhotoStudioEvent, assignmentId, sourceVisualFilename, getCommonAnalyticsData])

  const setSelectedStyle = useCallback((selectedStyle: StyleType) => {
    updateVirtualStagingState({ selectedStyle })
    logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_VISUAL_STAGING_STYLE_CHANGED, {
      selectedStyle,
      roomType: virtualStagingState.roomType,
      assignmentId,
      sourceVisualFilename,
    })
    // Track edition selected
    getCommonAnalyticsData(undefined, virtualStagingState.roomType || undefined).then(commonData => {
      logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_EDITION_SELECTED, {
        editionType: EditionType.VIRTUAL_STAGING,
        editionSettings: {
          roomType: virtualStagingState.roomType,
          selectedStyle,
        },
        ...commonData,
      })
    }).catch(() => {
      logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_EDITION_SELECTED, {
        editionType: EditionType.VIRTUAL_STAGING,
        editionSettings: {
          roomType: virtualStagingState.roomType,
          selectedStyle,
        },
        sourceVisualFilename,
      })
    })
  }, [updateVirtualStagingState, logPhotoStudioEvent, virtualStagingState.roomType, assignmentId, sourceVisualFilename, getCommonAnalyticsData])

  const setKeepFloorUntouched = useCallback((keepFloorUntouched: boolean) => {
    updateVirtualStagingState({ keepFloorUntouched })
    logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_VISUAL_STAGING_FLOOR_TOGGLE, {
      keepFloorUntouched,
      assignmentId,
      sourceVisualFilename,
    })
  }, [updateVirtualStagingState, logPhotoStudioEvent, assignmentId, sourceVisualFilename])

  // AI label setters (EU AI Act) - available in all Photo Studio tools
  const setAiLabelToggle = useCallback((showAiLabel: boolean) => {
    setAiLabelState(prev => ({ ...prev, showAiLabel }))
  }, [])

  const setAiLabelType = useCallback((labelType: AiLabelType) => {
    // Size ranges barely overlap across types - carrying a fraction over renders at a clamp
    // bound instead of the type's default, so the size resets; the chosen position stays
    setAiLabelState(prev => ({ ...prev, labelType, sizeFraction: null }))
  }, [])

  const setAiLabelText = useCallback((labelText: string) => {
    setAiLabelState(prev => ({ ...prev, labelText }))
  }, [])

  const setAiLabelColour = useCallback((colour: AiLabelColour) => {
    setAiLabelState(prev => ({ ...prev, colour }))
  }, [])

  const setAiLabelOpacity = useCallback((opacity: number) => {
    // Enforce the legibility floor here, not only in the slider - state is the single gate
    const clamped = Math.min(AI_LABEL_OPACITY_MAX, Math.max(AI_LABEL_OPACITY_MIN, opacity))
    setAiLabelState(prev => ({ ...prev, opacity: clamped }))
  }, [])

  const setAiLabelPosition = useCallback((position: AiLabelPosition | null) => {
    // State is the single gate: only finite, normalised coordinates may persist
    const safe = position && Number.isFinite(position.x) && Number.isFinite(position.y)
      ? { x: Math.min(1, Math.max(0, position.x)), y: Math.min(1, Math.max(0, position.y)) }
      : null
    setAiLabelState(prev => ({ ...prev, position: safe }))
  }, [])

  const setAiLabelSizeFraction = useCallback((sizeFraction: number | null) => {
    // Range clamping is type-dependent (aiLabelLayout) - the gate here rejects non-finite values
    setAiLabelState(prev => ({ ...prev, sizeFraction: sizeFraction != null && Number.isFinite(sizeFraction) ? sizeFraction : null }))
  }, [])

  const resetAiLabelPlacement = useCallback(() => {
    setAiLabelState(prev => ({ ...prev, position: null, sizeFraction: null }))
  }, [])

  const setVisualStagingMaskData = useCallback((maskData: Blob | null) => {
    updateVirtualStagingState({ maskData })
  }, [updateVirtualStagingState])

  // Lab SDK engine: the view switch reads Before | Compare | After once there is an earlier version to compare with (an
  // edit or a generation undo could go back to), and is hidden otherwise — no Editing | Original. Shared by every tool's
  // preview (Stage, Enhance, Erase and the Adjust toolbar). Compare is a per-result choice: another tool or a new run
  // starts on the plain image again.
  const isLabCompareAvailable = isLabSdkEngine && photoStudioHistory.historyState.canUndo
  const [isLabCompare, setIsLabCompare] = useState(false)
  useEffect(() => {
    setIsLabCompare(false)
  }, [activeFeature, isProcessingImage])
  useEffect(() => {
    if (!isLabSdkEngine || isLabCompareAvailable) return
    // Nothing left to compare (e.g. undone back to the first version): back to the plain image
    setIsLabCompare(false)
    setExpandedVisualType(ExpandedVisualType.EDITED)
  }, [isLabSdkEngine, isLabCompareAvailable])

  /**
   * Lab engine, Before view: the tools stay usable and Generate reuses the shown result's Before (the chevron's Before,
   * which the CTA then hides) — nothing is undone. `labWorkingImage` is the version the tools work on.
   */
  const isLabBeforeView = isLabSdkEngine && isOriginalVisualType && isLabCompareAvailable
  const labWorkingImage = resolveLabWorkingImage({
    isLabSdkEngine,
    isOriginalVisualType,
    isLabCompareAvailable,
    before: labRunSources.before,
    previewImage,
  })

  const isMagicEraserTool = useMemo(() => activeFeature === PhotoStudioSidebarOption.ERASER, [activeFeature])
  // Lab engine, eraser: click-to-select objects — the photo it works on (see labWorkingImage) is uploaded and its objects
  // detected, and Generate reuses that upload. The photo Photo Studio opened with is detected from the start, silently;
  // a later one once the eraser opens on it. The opened photo only once its local copy is on stage: its signed URL is
  // swapped for that copy right after opening, and each would be uploaded and detected
  const { hasWorkspaceActiveSubscription } = useWorkspacePlan()
  const labObjectsDetection = resolveLabObjectsDetection({
    isLabSdkEngine,
    hasSubscription: hasWorkspaceActiveSubscription,
    isEraserOpen: isMagicEraserTool,
    isOpenedPhoto: isLocalUrl(convertedImageUrl) && labWorkingImage === convertedImageUrl,
  })
  const labEraser = useLabEraserObjects({
    ...labObjectsDetection,
    engine: labEngine,
    imageUrl: labWorkingImage,
    getImageFile: getPreviewFileForAutoSave,
    uploadVisual: uploadTemporaryVisualFile,
  })

  const isVirtualStaging = useMemo(() => activeFeature === PhotoStudioSidebarOption.STAGING, [activeFeature])
  /** The Adjust editor is on the canvas: Adjust, or its Blur tool on its own */
  const isAdjustTool = useMemo(() => isAdjustEditorOption(activeFeature), [activeFeature])
  const isBlurTool = activeFeature === PhotoStudioSidebarOption.BLUR

  /**
   * "Combine tools" (One tool | Combine tools, on top of the tools column): Stage, Enhance, Erase and Adjust as sections
   * of one list, combined into one Generate. Lab SDK engine only — ai-visuals cannot merge tools into one job. Non-
   * subscribers get it too, locked like the One tool panels (switches off, Subscribe in Generate's place). Remembered
   * per browser.
   */
  const isAllFeaturesViewAvailable = isLabSdkEngine
  const [allFeaturesViewPreference, setAllFeaturesViewPreference] = useState(() => readStoredFlag(ALL_FEATURES_STORAGE_KEYS.view))
  /** Which Combine tools sections are expanded, kept for this Photo Studio session (null until the view first opens) */
  const [labSectionsOpen, setLabSectionsOpen] = useState<LabSectionsOpen | null>(null)
  const isAllFeaturesView = isAllFeaturesViewAvailable && allFeaturesViewPreference

  /** `source`: the One tool | Combine tools control, or the first-run hint's "Try it" */
  const setAllFeaturesView = useCallback((on: boolean, source: 'switch' | 'hint' = 'switch') => {
    setAllFeaturesViewPreference(on)
    writeStoredFlag(ALL_FEATURES_STORAGE_KEYS.view, on)
    logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_ALL_FEATURES_VIEW_TOGGLED, { isAllFeaturesView: on, source, activeFeature, engine: LAB_SDK_ENGINE })
  }, [activeFeature, logPhotoStudioEvent])

  // Visual Staging validation logic
  const isVirtualStagingFormValid = useCallback(() => {
    return !!(
      virtualStagingState.roomType &&
      virtualStagingState.selectedStyle
    )
  }, [virtualStagingState.roomType, virtualStagingState.selectedStyle])

  const canGenerateVisualStaging = useCallback(() => {
    return isVirtualStagingFormValid() && !virtualStagingState.isGeneratingStaging
  }, [isVirtualStagingFormValid, virtualStagingState.isGeneratingStaging])

  // Check if any Visual Staging settings have been changed from defaults
  const hasVisualStagingSettingsChanged = useCallback(() => {
    return !!(
      virtualStagingState.roomType || // Room type selected
      virtualStagingState.selectedStyle || // Style selected
      virtualStagingState.keepFloorUntouched || // Floor preservation enabled
      aiLabelState.showAiLabel // AI label enabled
    )
  }, [virtualStagingState.roomType, virtualStagingState.selectedStyle, virtualStagingState.keepFloorUntouched, aiLabelState.showAiLabel])

  const shouldShowMaskingTools = useCallback(() => {
    return virtualStagingState.processingMode === ProcessingMode.PRESERVE_ZONE ||
      virtualStagingState.processingMode === ProcessingMode.REPLACE_ZONE
  }, [virtualStagingState.processingMode])

  // Define getLatestPreviewFilename separately so it can be used in dependencies
  const getLatestPreviewFilename = useCallback(() => latestPreviewFilenameRef.current, [])

  // Memoize context return value to prevent unnecessary re-renders
  return useMemo(() => ({
    sourceVisualFilename,
    imageUrl,
    convertedImageUrl,
    assignmentId,
    uploadAIMask,
    isVirtualStaging,
    isMagicEraserTool,
    isAdjustTool,
    isBlurTool,
    handleUploadImage,
    handleProcessMagicEraser,
    isProcessingImage,
    setIsProcessingImage,
    aiProcessingStep,
    setAIProcessingStep,
    labProgress,
    setLabProgress,
    labScene,
    setLabScene,
    labSceneImageRef,
    showLabSceneImage,
    labStagePlan,
    setLabStagePlan,
    labReveal,
    setLabReveal,
    isLabSdkEngine,
    labRunSources,
    isLabCompareAvailable,
    isLabCompare,
    setIsLabCompare,
    labStageSettings,
    updateLabStageSettings,
    labEnhanceSettings,
    updateLabEnhanceSettings,
    isAllFeaturesViewAvailable,
    isAllFeaturesView,
    labSectionsOpen,
    setLabSectionsOpen,
    setAllFeaturesView,
    labEraserObjects: labEraser.objects,
    getLabDetectedSource: labEraser.getDetectedSource,
    isLabBeforeView,
    labWorkingImage,
    labEngine,
    uploadLabMask,
    isSaving,
    onExitStudio,
    editorRef,
    isPinturaReplacement,
    adjustEditorRef,
    adjustHasEdits,
    setAdjustHasEdits,
    isFileVersioned,
    setIsFileVersioned,
    handleUpdate,
    activeNavTab,
    setActiveNavTab,
    selectedPinturaFeature,
    setSelectedPinturaFeature,
    logPhotoStudioEvent,
    getCommonAnalyticsData,
    setSelectedAIFeature,
    selectedAIFeature,
    activeFeature,
    pinturaHistory,
    setPinturaHistory,
    previewImage,
    setPreviewImage,
    tag,
    setTag,
    isToolSwitching,
    toolSwitchStep,
    toolSwitchProgress,
    switchEditingMode,
    previousJobId,
    currentMaskFilename,
    virtualStagingJobId,
    setVirtualStagingJobId,
    hasAIEraserCompleted,
    setHasAIEraserCompleted,
    hasVisualStagingCompleted,
    setHasVisualStagingCompleted,
    hasEnhanceCompleted,
    setHasEnhanceCompleted,
    onGenerateStarted,
    convertedPinturaTag,
    latestPreviewFilename,
    isSaveAndRedirecting,
    clearUndoRedoHistoryRef,
    hasMaskDrawnRef,
    maskAnalyticsRef,
    // Visual Staging state and functions
    virtualStagingState,
    setVirtualStagingState,
    updateVirtualStagingState,
    setGenerationType,
    setProcessingMode,
    setRoomType,
    setSelectedStyle,
    setKeepFloorUntouched,
    setVisualStagingMaskData,
    isVirtualStagingFormValid,
    canGenerateVisualStaging,
    hasVisualStagingSettingsChanged,
    shouldShowMaskingTools,
    convertImageToDataURL,
    hasPendingChanges,
    getLatestPreviewFilename,
    getPreviewFileForAutoSave,
    uploadTemporaryVisualFile,
    setLatestPreviewFilename,
    // AI label state and functions (EU AI Act, staging flows only)
    aiLabelState,
    setAiLabelToggle,
    setAiLabelType,
    setAiLabelText,
    setAiLabelColour,
    setAiLabelOpacity,
    setAiLabelPosition,
    setAiLabelSizeFraction,
    resetAiLabelPlacement,
    expandedVisualType,
    setExpandedVisualType,
    isOriginalVisualType,
    imageGenerationSequenceNumber,
    setImageGenerationSequenceNumber,
    photoStudioHistory

  }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      sourceVisualFilename,
      imageUrl,
      convertedImageUrl,
      assignmentId,
      uploadAIMask,
      isVirtualStaging,
      isMagicEraserTool,
      isAdjustTool,
      isBlurTool,
      handleUploadImage,
      handleProcessMagicEraser,
      isProcessingImage,
      aiProcessingStep,
      labProgress,
      labScene,
      labStagePlan,
      labReveal,
      isLabSdkEngine,
      labRunSources,
      isLabCompareAvailable,
      isLabCompare,
      labStageSettings,
      updateLabStageSettings,
      labEnhanceSettings,
      updateLabEnhanceSettings,
      isAllFeaturesViewAvailable,
      isAllFeaturesView,
      labSectionsOpen,
      setAllFeaturesView,
      labEraser.objects,
      labEraser.getDetectedSource,
      isLabBeforeView,
      labWorkingImage,
      labEngine,
      uploadLabMask,
      isSaving,
      onExitStudio,
      editorRef,
      isPinturaReplacement,
      adjustEditorRef,
      adjustHasEdits,
      setAdjustHasEdits,
      isFileVersioned,
      handleUpdate,
      activeNavTab,
      selectedPinturaFeature,
      logPhotoStudioEvent,
      getCommonAnalyticsData,
      selectedAIFeature,
      activeFeature,
      pinturaHistory,
      previewImage,
      tag,
      isToolSwitching,
      toolSwitchStep,
      toolSwitchProgress,
      switchEditingMode,
      previousJobId,
      currentMaskFilename,
      virtualStagingJobId,
      hasAIEraserCompleted,
      hasVisualStagingCompleted,
      hasEnhanceCompleted,
      onGenerateStarted,
      convertedPinturaTag,
      latestPreviewFilename,
      isSaveAndRedirecting,
      hasMaskDrawnRef,
      virtualStagingState,
      setVirtualStagingState,
      updateVirtualStagingState,
      setGenerationType,
      setProcessingMode,
      setRoomType,
      setSelectedStyle,
      setKeepFloorUntouched,
      setVisualStagingMaskData,
      isVirtualStagingFormValid,
      canGenerateVisualStaging,
      hasVisualStagingSettingsChanged,
      shouldShowMaskingTools,
      convertImageToDataURL,
      hasPendingChanges,
      getLatestPreviewFilename,
      getPreviewFileForAutoSave,
      uploadTemporaryVisualFile,
      setLatestPreviewFilename,
      aiLabelState,
      setAiLabelToggle,
      setAiLabelType,
      setAiLabelText,
      setAiLabelColour,
      setAiLabelOpacity,
      setAiLabelPosition,
      setAiLabelSizeFraction,
      resetAiLabelPlacement,
      expandedVisualType,
      isOriginalVisualType,
      imageGenerationSequenceNumber,
      photoStudioHistory
    ])
})
